// React
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
// MUI
import { Grid, Box, Button, Container, Divider } from "@mui/material";
import Iconify from "../../../components/iconify/Iconify";
// Constants
import EntityConstants from "../../../configs/EntityConstants";
import MessageContants from "../../../configs/MessageContants";
// Service
import { get, post, put, del, gen_link } from "../../../services/FetchHelper";
import SpinService from "../../../services/SpinService";
// Utils
import { voucherTypes, SPLITUP_LINES_TABLE_HEADS } from "./Voucher.utils";
// Styles
import { StyledDrawerForm } from "../../../configs/GoStyles";
//Third party
import { Form, Formik } from "formik";
import toast from "react-hot-toast";
import GoForm from "../../../components/Inputs/GoForm";
import PermissionConstants from "../../../configs/PermissionConstants";
import usePermissions from "../../../hooks/usePermissions";

import VoucherLines from "./VoucherLines";
import GoDialog from "../../../common/GoDialog/GoDialog";

const Voucher = ({ id, voucherType, onClose }) => {
    const permissions = usePermissions();
    const spinService = SpinService();
    const [voucher, setVoucher] = useState(voucherTypes[voucherType].state);
    const [schema, setSchema] = useState(voucherTypes[voucherType].schema);
    const [formFields, setFormFields] = useState(voucherTypes[voucherType].formFields);
    const [voucherLines, setVoucherLines] = useState([]);
    const [voucherSplitUpLines, setVoucherSplitUpLines] = useState([]);
    const [voucherId, setVoucherId] = useState(id);
    const [splitUpLines, setSplitUpLines] = useState([]);
    const [confirm, setConfirm] = useState(false);

    const attachOptions = (name, options) => {
        setFormFields(prevFormFields =>
            prevFormFields.map(field =>
                field.name === name && (field.type === "select" || field.type === "autocomplete")
                    ? { ...field, options: options }
                    : field
            )
        );
    };

    const handleCommonCodeChange = async () => {
        spinService.on();
        const response = await get(EntityConstants.COMMON_CODES, `types=voucher_debit_ledgers`);
        const data = await response.json();
        attachOptions("credit_ledger_id", data.voucher_debit_ledgers);
        spinService.off();
    };

    const handleAccountChange = async () => {
        spinService.on();
        const response = await get(EntityConstants.ACCOUNTS, "max=all");
        const data = await response.json();
        attachOptions("credit_ledger_id", data);
        spinService.off();
    };

    const handleSplitUpChange = async (voucherId) => {
        spinService.on();
        const response = await get(EntityConstants.SPLITUP_LINES_BY_VOUCHER + `/${voucherId}`);
        const data = await response.json();
        setSplitUpLines(data);
        spinService.off();
    }

    const handleVoucherChange = async (voucherId) => {
        spinService.on();
        const response = await get(EntityConstants.VOUCHERS + `/${voucherId}`);
        const data = await response.json();
        setVoucher(data);
        spinService.off();
        setVoucherLines(data.voucher_lines.filter((vl) => vl.type !== "SplitUp"));
        let splitUps = data.voucher_lines.filter((vl) => vl.type === "SplitUp")
        setVoucherSplitUpLines(splitUps);
        if (voucherType === "Prepayment Voucher" && splitUps.length > 0) {
            handleSplitUpChange(voucherId);
        }
    };

    const handleSubmit = async (values) => {
        spinService.on();
        const isNewVoucher = values.id === undefined;
        if (isNewVoucher) {
            values.type = voucherType;
        }
        const url = isNewVoucher ? EntityConstants.VOUCHERS : EntityConstants.VOUCHERS + `/${values.id}`;
        const method = isNewVoucher ? post : put;
        const response = await method(url, { voucher: values });
        if ([200, 201].indexOf(response.status) > -1) {
            const data = await response.json();
            toast.success(isNewVoucher ? MessageContants.VOUCHER_CREATED : MessageContants.VOUCHER_UPDATED);
            handleVoucherChange(data.id);
            setVoucherId(data.id);
            setVoucherLines(data.voucher_lines);

        }
        spinService.off();
    };

    const handlePdfDownloadChange = async () => {
        const response = await fetch(gen_link(`${EntityConstants.VOUCHER_PDF_DOWNLOAD}/${voucherId}`));
        const pdfBlob = await response.blob();
        const pdfBlobUrl = URL.createObjectURL(pdfBlob);
        const previewWindow = window.open(pdfBlobUrl, '_blank');

        // const link = document.createElement('a');
        // link.href = gen_link(`${EntityConstants.VOUCHER_PDF_DOWNLOAD}/${voucherId}`);
        // link.target = '_blank';
        // link.click();
    };

    const handleClose = async (res) => {
        if (res) {
            spinService.on();
            const response = await del(EntityConstants.VOUCHERS + `/${voucher.id}`);
            if (response.status === 200) {
                toast.success(MessageContants.VOUCHER_DELETED);
                onClose();
            }
            spinService.off();
        }
        setConfirm(false);
    };

    const handleStatusUpdateHandler = (receipt_header) => {
        handleSubmit(receipt_header)
    };

    useEffect(() => {
        if (voucherType === "PDC Bank Payment") {
            handleAccountChange();
        } else {
            handleCommonCodeChange();
        }
        if (id !== undefined) {
            setVoucherId(Number(id))
            handleVoucherChange(Number(id));
        }
    }, [id]);

    return (
        <>
            <Formik
                enableReinitialize
                initialValues={voucher}
                validationSchema={schema}
                onSubmit={(values) => handleSubmit(values)}
            >
                {({
                    values,
                    touched,
                    errors,
                    handleChange,
                    handleSubmit,
                }) => (
                    <Form noValidate onSubmit={handleSubmit}>
                        <StyledDrawerForm>
                            <Grid
                                container
                                // maxWidth="md"
                                rowSpacing={1}
                                columnSpacing={{ xs: 1, sm: 2, md: 2 }}
                            >
                                {formFields.map((formField, index) => {
                                    return (
                                        <Grid key={index} item xs={12} sm={12} md={6} lg={6}>
                                            <GoForm
                                                config={formField}
                                                values={values}
                                                touched={touched}
                                                errors={errors}
                                                handleChange={(e) => { handleChange(e) }}
                                            />
                                        </Grid>
                                    );
                                })}
                            </Grid>
                            <Box sx={{ p: 2, textAlign: "right" }}>
                                <Button
                                    variant="contained"
                                    color="error"
                                    startIcon={<Iconify icon="eva:close-circle-outline" />}
                                    onClick={() => onClose()}
                                >
                                    Close
                                </Button>
                                &nbsp;&nbsp;
                                {(values.id !== undefined && values.type == "PDC Bank Payment") &&
                                    <Button
                                        variant="contained"
                                        startIcon={<Iconify icon="ic:twotone-delete" />}
                                        onClick={() => setConfirm(true)}
                                    >
                                        Delete
                                    </Button>
                                }
                                &nbsp;&nbsp;
                                {(values.id !== undefined && values.type == "PDC Bank Payment" && permissions.has(PermissionConstants.PDC_BANK_PAYMENT_UPDATE)) &&
                                    <Button
                                        variant="contained"
                                        startIcon={<Iconify icon="mdi:tick-circle-outline" />}
                                        onClick={() => handleStatusUpdateHandler({ id: values.id, status: "Active" })}
                                        disabled={voucher.status === "Active"}
                                    >
                                        {voucher.status === "Active" ? 'Activated' : 'Activate'}
                                    </Button>
                                }
                                &nbsp;&nbsp;
                                {
                                    (
                                        (values.id === undefined && (
                                            (voucherType === "Payment Voucher" && permissions.has(PermissionConstants.PAYMENT_VOUCHER_CREATE)) ||
                                            (voucherType === "Journal Voucher" && permissions.has(PermissionConstants.JOURNAL_VOUCHER_CREATE)) ||
                                            (voucherType === "Prepayment Voucher" && permissions.has(PermissionConstants.PREPAYMENT_VOUCHER_CREATE)) ||
                                            (voucherType === "PDC Bank Payment" && permissions.has(PermissionConstants.PDC_BANK_PAYMENT_CREATE))
                                        )) ||
                                        (values.id !== undefined && (
                                            (voucherType === "Payment Voucher" && permissions.has(PermissionConstants.PAYMENT_VOUCHER_UPDATE)) ||
                                            (voucherType === "Journal Voucher" && permissions.has(PermissionConstants.JOURNAL_VOUCHER_UPDATE)) ||
                                            (voucherType === "Prepayment Voucher" && permissions.has(PermissionConstants.PREPAYMENT_VOUCHER_UPDATE)) ||
                                            (voucherType === "PDC Bank Payment" && permissions.has(PermissionConstants.PDC_BANK_PAYMENT_UPDATE))
                                        ))
                                    ) &&
                                    <Button
                                        variant="contained"
                                        startIcon={<Iconify icon="eva:save-fill" />}
                                        type="submit"
                                    >
                                        Save
                                    </Button>
                                }
                                &nbsp;&nbsp;
                                {((values.id !== undefined && voucherType === "Payment Voucher" && permissions.has(PermissionConstants.PAYMENT_VOUCHER_DOWNLOAD)) ||
                                    (values.id !== undefined && voucherType === "Journal Voucher" && permissions.has(PermissionConstants.JOURNAL_VOUCHER_DOWNLOAD)) ||
                                    (values.id !== undefined && voucherType === "Prepayment Voucher" && permissions.has(PermissionConstants.PREPAYMENT_VOUCHER_DOWNLOAD)) ||
                                    (values.id !== undefined && voucherType === "PDC Bank Payment" && permissions.has(PermissionConstants.PDC_BANK_PAYMENT_DOWNLOAD))) &&
                                    <Button
                                        variant="contained"
                                        startIcon={<Iconify icon="typcn:download" />}
                                        onClick={handlePdfDownloadChange}
                                    >
                                        Download
                                    </Button>
                                }
                            </Box>
                        </StyledDrawerForm>
                    </Form>
                )}
            </Formik>
            <Divider />
            <br />
            {voucherId !== undefined && <VoucherLines voucherId={voucherId} voucherType={voucherType} voucherLines={voucherLines} reload={() => handleVoucherChange(voucherId)} />}
            <br />
            {voucherId !== undefined && voucherType === "Prepayment Voucher" && <VoucherLines voucherId={voucherId} voucherType="SplitUp" voucherLines={voucherSplitUpLines} reload={() => handleVoucherChange(voucherId)} />}
            <br />
            {voucherId !== undefined && voucherType === "Prepayment Voucher" && splitUpLines.length > 0 && <VoucherLines voucherId={voucherId} voucherType="SplitUp Lines" voucherLines={splitUpLines} reload={() => handleVoucherChange(voucherId)} />}
            <GoDialog
                open={confirm}
                title=""
                content="Are you sure you want to delete?"
                positive="Yes"
                negative="No"
                handleClose={(res) => handleClose(res)}
            />
        </>
    );
};

Voucher.propTypes = {
    id: PropTypes.number,
    voucherType: PropTypes.string,
    onClose: PropTypes.any,
};

export default Voucher;